import { useState, useCallback, useEffect, useRef } from "react";
import { useGlobalAPI } from "../../../../hooks/useGlobalAPI";
import { useOcelMetadata } from "../../../../stores/OcelContext";
import { useGlobalFilters } from "../../../../stores/GlobalFilterContext/GlobalFilterContext";
import {
  ANALYSIS_TYPE_CROSS,
  ANALYSIS_TYPE_OBJECT,
  ANALYSIS_TYPE_VARIANT,
  COUNT_METRIC,
  DEFAULT_TITLE,
} from "../constants";

/**
 * State and data-fetching hook for the ABC/XYZ Matrix panel.
 *
 * All classification happens in the backend (/abc-xyz endpoints); this hook
 * only manages configuration, persistence and the fetched summaries. Global
 * filters are applied automatically via useGlobalAPI, and object-analysis
 * dropdown options come from the dynamic /abc-xyz/object-config-options
 * metadata — nothing dataset-specific is hard-coded.
 */
const useABCXYZAnalysis = (activeFile, initialSettings = null) => {
  const api = useGlobalAPI(activeFile);
  const metadata = useOcelMetadata(activeFile);
  const { filterState } = useGlobalFilters();
  const configKey = `abcXyzAnalysisConfig_${activeFile || "default"}`;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [variantSummary, setVariantSummary] = useState(null);
  const [objectSummary, setObjectSummary] = useState(null);
  const [crossSummary, setCrossSummary] = useState(null);
  const [configOptions, setConfigOptions] = useState(null);
  const [objectTypes, setObjectTypes] = useState([]);

  // TileConfig (persisted)
  const [title, setTitle] = useState(DEFAULT_TITLE);
  const [analysisType, setAnalysisType] = useState(ANALYSIS_TYPE_VARIANT);
  // Variant mode configuration
  const [variantObjectType, setVariantObjectType] = useState("");
  const [variantMode, setVariantMode] = useState("exact");
  // Object mode configuration
  const [objectType, setObjectType] = useState("");
  const [groupByAttribute, setGroupByAttribute] = useState("");
  const [abcMetric, setAbcMetric] = useState(COUNT_METRIC);
  const [abcAggregation, setAbcAggregation] = useState("sum");
  const [dateField, setDateField] = useState("");
  const [dateFieldOrigin, setDateFieldOrigin] = useState("");
  const [xyzBasis, setXyzBasis] = useState("count");
  const [xyzGranularity, setXyzGranularity] = useState("month");
  const [fiscalYearStartMonth, setFiscalYearStartMonth] = useState(1);
  const [abcPeriod, setAbcPeriod] = useState("last_12_months");
  const [objectConfigCollapsed, setObjectConfigCollapsed] = useState(false);

  const latestRequestIdRef = useRef(0);

  // Restore TileConfig from localStorage
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(configKey);
      if (!raw) return;
      const cfg = JSON.parse(raw);
      if (typeof cfg.title === "string") setTitle(cfg.title);
      if (
        cfg.analysisType === ANALYSIS_TYPE_VARIANT ||
        cfg.analysisType === ANALYSIS_TYPE_OBJECT ||
        cfg.analysisType === ANALYSIS_TYPE_CROSS
      )
        setAnalysisType(cfg.analysisType);
      if (typeof cfg.variantObjectType === "string")
        setVariantObjectType(cfg.variantObjectType);
      if (typeof cfg.variantMode === "string") setVariantMode(cfg.variantMode);
      if (typeof cfg.objectType === "string") setObjectType(cfg.objectType);
      if (typeof cfg.groupByAttribute === "string")
        setGroupByAttribute(cfg.groupByAttribute);
      if (typeof cfg.abcMetric === "string") setAbcMetric(cfg.abcMetric);
      if (typeof cfg.abcAggregation === "string")
        setAbcAggregation(cfg.abcAggregation);
      if (typeof cfg.dateField === "string") setDateField(cfg.dateField);
      if (typeof cfg.dateFieldOrigin === "string")
        setDateFieldOrigin(cfg.dateFieldOrigin);
      if (typeof cfg.xyzBasis === "string") setXyzBasis(cfg.xyzBasis);
      if (typeof cfg.xyzGranularity === "string")
        setXyzGranularity(cfg.xyzGranularity);
      if (typeof cfg.fiscalYearStartMonth === "number")
        setFiscalYearStartMonth(cfg.fiscalYearStartMonth);
      if (typeof cfg.abcPeriod === "string") setAbcPeriod(cfg.abcPeriod);
      if (typeof cfg.objectConfigCollapsed === "boolean")
        setObjectConfigCollapsed(cfg.objectConfigCollapsed);
    } catch {
      /* no-op */
    }
  }, [configKey]);

  // Restore from centralised dashboard config (overrides localStorage on mount)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!initialSettings) return;
    const s = initialSettings;
    if (s.title !== undefined) setTitle(s.title);
    if (s.analysisType !== undefined) setAnalysisType(s.analysisType);
    if (s.variantObjectType !== undefined)
      setVariantObjectType(s.variantObjectType);
    if (s.variantMode !== undefined) setVariantMode(s.variantMode);
    if (s.objectType !== undefined) setObjectType(s.objectType);
    if (s.groupByAttribute !== undefined)
      setGroupByAttribute(s.groupByAttribute);
    if (s.abcMetric !== undefined) setAbcMetric(s.abcMetric);
    if (s.abcAggregation !== undefined) setAbcAggregation(s.abcAggregation);
    if (s.dateField !== undefined) setDateField(s.dateField);
    if (s.dateFieldOrigin !== undefined) setDateFieldOrigin(s.dateFieldOrigin);
    if (s.xyzBasis !== undefined) setXyzBasis(s.xyzBasis);
    if (s.xyzGranularity !== undefined) setXyzGranularity(s.xyzGranularity);
    if (s.fiscalYearStartMonth !== undefined)
      setFiscalYearStartMonth(s.fiscalYearStartMonth);
    if (s.abcPeriod !== undefined) setAbcPeriod(s.abcPeriod);
    if (s.objectConfigCollapsed !== undefined)
      setObjectConfigCollapsed(s.objectConfigCollapsed);
  }, []);

  // Persist TileConfig
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        configKey,
        JSON.stringify({
          title,
          analysisType,
          variantObjectType,
          variantMode,
          objectType,
          groupByAttribute,
          abcMetric,
          abcAggregation,
          dateField,
          dateFieldOrigin,
          xyzBasis,
          xyzGranularity,
          fiscalYearStartMonth,
          abcPeriod,
          objectConfigCollapsed,
        }),
      );
    } catch {
      /* no-op */
    }
  }, [
    configKey,
    title,
    analysisType,
    variantObjectType,
    variantMode,
    objectType,
    groupByAttribute,
    abcMetric,
    abcAggregation,
    dateField,
    dateFieldOrigin,
    xyzBasis,
    xyzGranularity,
    fiscalYearStartMonth,
    abcPeriod,
    objectConfigCollapsed,
  ]);

  // Reactive metadata sync from OcelContext (object type list for the
  // variant-mode settings). Read-time staleness is handled by the
  // useOcelMetadata(activeFile) guard — never cleared in a reset effect.
  useEffect(() => {
    if (!metadata?.object_types) return;
    setObjectTypes(Object.keys(metadata.object_types));
  }, [metadata, activeFile]);

  // Auto-select the first object type for variant mode
  useEffect(() => {
    if (!variantObjectType && objectTypes.length > 0) {
      setVariantObjectType(objectTypes[0]);
    }
  }, [objectTypes, variantObjectType]);

  // Reset fetched output on dataset switch (config is per-file via configKey)
  useEffect(() => {
    setVariantSummary(null);
    setObjectSummary(null);
    setCrossSummary(null);
    setConfigOptions(null);
    setError(null);
  }, [activeFile]);

  const fetchConfigOptions = useCallback(async () => {
    if (!activeFile) return null;
    try {
      const options = await api.getRaw(
        "/abc-xyz/object-config-options",
        { file_type: activeFile },
        {},
        "ABC/XYZ Config Options",
      );
      setConfigOptions(options);
      return options;
    } catch (err) {
      setError(err.message || "Failed to load object configuration options");
      return null;
    }
  }, [api, activeFile]);

  const fetchVariantSummary = useCallback(
    async (overrides = {}) => {
      const effectiveObjectType =
        overrides.variantObjectType ?? variantObjectType;
      if (!activeFile || !effectiveObjectType) return false;

      setLoading(true);
      setError(null);
      const requestId = ++latestRequestIdRef.current;
      try {
        const response = await api.post(
          "/abc-xyz/variant-summary",
          {
            object_type: effectiveObjectType,
            variant_mode: overrides.variantMode ?? variantMode,
          },
          {},
          "ABC/XYZ Variant Summary",
        );
        if (requestId !== latestRequestIdRef.current) return true;
        setVariantSummary(response);
        return true;
      } catch (err) {
        if (requestId !== latestRequestIdRef.current) return false;
        setVariantSummary(null);
        setError(err.message || "Failed to load variant summary");
        return false;
      } finally {
        if (requestId === latestRequestIdRef.current) setLoading(false);
      }
    },
    [activeFile, api, variantObjectType, variantMode],
  );

  const fetchObjectSummary = useCallback(
    async (overrides = {}) => {
      const cfg = {
        objectType: overrides.objectType ?? objectType,
        groupByAttribute: overrides.groupByAttribute ?? groupByAttribute,
        abcMetric: overrides.abcMetric ?? abcMetric,
        abcAggregation: overrides.abcAggregation ?? abcAggregation,
        dateField: overrides.dateField ?? dateField,
        dateFieldOrigin: overrides.dateFieldOrigin ?? dateFieldOrigin,
        xyzBasis: overrides.xyzBasis ?? xyzBasis,
        xyzGranularity: overrides.xyzGranularity ?? xyzGranularity,
        fiscalYearStartMonth:
          overrides.fiscalYearStartMonth ?? fiscalYearStartMonth,
        abcPeriod: overrides.abcPeriod ?? abcPeriod,
      };
      if (
        !activeFile ||
        !cfg.objectType ||
        !cfg.groupByAttribute ||
        !cfg.dateField
      )
        return false;

      setLoading(true);
      setError(null);
      const requestId = ++latestRequestIdRef.current;
      try {
        const response = await api.post(
          "/abc-xyz/object-summary",
          {
            object_type: cfg.objectType,
            group_by_attribute: cfg.groupByAttribute,
            abc_metric: cfg.abcMetric,
            abc_aggregation: cfg.abcAggregation,
            date_field: cfg.dateField,
            date_field_origin:
              cfg.dateFieldOrigin && cfg.dateFieldOrigin !== cfg.objectType
                ? cfg.dateFieldOrigin
                : null,
            xyz_basis: cfg.abcMetric === COUNT_METRIC ? "count" : cfg.xyzBasis,
            xyz_granularity: cfg.xyzGranularity,
            fiscal_year_start_month: cfg.fiscalYearStartMonth,
            abc_period: cfg.abcPeriod,
          },
          {},
          "ABC/XYZ Object Summary",
        );
        if (requestId !== latestRequestIdRef.current) return true;
        setObjectSummary(response);
        return true;
      } catch (err) {
        if (requestId !== latestRequestIdRef.current) return false;
        setObjectSummary(null);
        setError(err.message || "Failed to load object summary");
        return false;
      } finally {
        if (requestId === latestRequestIdRef.current) setLoading(false);
      }
    },
    [
      activeFile,
      api,
      objectType,
      groupByAttribute,
      abcMetric,
      abcAggregation,
      dateField,
      dateFieldOrigin,
      xyzBasis,
      xyzGranularity,
      fiscalYearStartMonth,
    ],
  );

  // Cross Matrix: one shared object type drives both the variant classification
  // (variant mode) and the object-group classification (the full object config).
  const fetchCrossSummary = useCallback(
    async (overrides = {}) => {
      const cfg = {
        objectType: overrides.objectType ?? objectType,
        variantMode: overrides.variantMode ?? variantMode,
        groupByAttribute: overrides.groupByAttribute ?? groupByAttribute,
        abcMetric: overrides.abcMetric ?? abcMetric,
        abcAggregation: overrides.abcAggregation ?? abcAggregation,
        dateField: overrides.dateField ?? dateField,
        dateFieldOrigin: overrides.dateFieldOrigin ?? dateFieldOrigin,
        xyzBasis: overrides.xyzBasis ?? xyzBasis,
        xyzGranularity: overrides.xyzGranularity ?? xyzGranularity,
        fiscalYearStartMonth:
          overrides.fiscalYearStartMonth ?? fiscalYearStartMonth,
        abcPeriod: overrides.abcPeriod ?? abcPeriod,
      };
      if (
        !activeFile ||
        !cfg.objectType ||
        !cfg.groupByAttribute ||
        !cfg.dateField
      )
        return false;

      setLoading(true);
      setError(null);
      const requestId = ++latestRequestIdRef.current;
      try {
        const response = await api.post(
          "/abc-xyz/cross-summary",
          {
            object_type: cfg.objectType,
            variant_mode: cfg.variantMode,
            group_by_attribute: cfg.groupByAttribute,
            abc_metric: cfg.abcMetric,
            abc_aggregation: cfg.abcAggregation,
            date_field: cfg.dateField,
            date_field_origin:
              cfg.dateFieldOrigin && cfg.dateFieldOrigin !== cfg.objectType
                ? cfg.dateFieldOrigin
                : null,
            xyz_basis: cfg.abcMetric === COUNT_METRIC ? "count" : cfg.xyzBasis,
            xyz_granularity: cfg.xyzGranularity,
            fiscal_year_start_month: cfg.fiscalYearStartMonth,
            abc_period: cfg.abcPeriod,
          },
          {},
          "ABC/XYZ Cross Summary",
        );
        if (requestId !== latestRequestIdRef.current) return true;
        setCrossSummary(response);
        return true;
      } catch (err) {
        if (requestId !== latestRequestIdRef.current) return false;
        setCrossSummary(null);
        setError(err.message || "Failed to load cross matrix");
        return false;
      } finally {
        if (requestId === latestRequestIdRef.current) setLoading(false);
      }
    },
    [
      activeFile,
      api,
      objectType,
      variantMode,
      groupByAttribute,
      abcMetric,
      abcAggregation,
      dateField,
      dateFieldOrigin,
      xyzBasis,
      xyzGranularity,
      fiscalYearStartMonth,
    ],
  );

  return {
    loading,
    error,
    variantSummary,
    objectSummary,
    crossSummary,
    configOptions,
    objectTypes,
    // TileConfig
    title,
    analysisType,
    variantObjectType,
    variantMode,
    objectType,
    groupByAttribute,
    abcMetric,
    abcAggregation,
    dateField,
    dateFieldOrigin,
    xyzBasis,
    xyzGranularity,
    fiscalYearStartMonth,
    abcPeriod,
    objectConfigCollapsed,
    // Setters
    setTitle,
    setAnalysisType,
    setVariantObjectType,
    setVariantMode,
    setObjectType,
    setGroupByAttribute,
    setAbcMetric,
    setAbcAggregation,
    setDateField,
    setDateFieldOrigin,
    setXyzBasis,
    setXyzGranularity,
    setFiscalYearStartMonth,
    setAbcPeriod,
    setObjectConfigCollapsed,
    // Actions
    fetchConfigOptions,
    fetchVariantSummary,
    fetchObjectSummary,
    fetchCrossSummary,
  };
};

export { useABCXYZAnalysis };
